import type { Settings } from "@go-novus/admin-api";
import { useAtomValue } from "jotai";
import { useEffect, useState } from "react";
import { resendSupportEmailAtom } from "#/features/settings";
import { storeConnectionAtom } from "#/features/store-connection";
import { formatWorkspaceTime } from "#/shared/lib/date/workspace-time";
import {
  Button,
  cn,
  Icon,
  Panel,
  StatusMark,
  TextField,
  type StatusMarkProps,
} from "@go-novus/ui";

import * as m from "#/paraglide/messages";

import type { DraftErrors } from "../lib/settings-draft";
import {
  readResendWaitSeconds,
  readSupportEmailVerificationView,
  type SupportEmailSendResult,
  type SupportEmailVerification,
  type SupportEmailVerificationView,
} from "../lib/support-email-verification";

export interface HandoffSectionProps {
  verification: SupportEmailVerification;
  savedEmail: string;
  errors: DraftErrors;
  handoff: Settings["handoff"];
  onChange: (next: Settings["handoff"]) => void;
}

const MARKS: Partial<
  Record<
    SupportEmailVerificationView["kind"],
    { tone: StatusMarkProps["tone"]; label: () => string }
  >
> = {
  unsaved: { tone: "neutral", label: m.settings_support_mark_unsaved },
  verified: { tone: "ready", label: m.settings_support_mark_verified },
  unsent: { tone: "review", label: m.settings_support_mark_unverified },
  pending: { tone: "review", label: m.settings_support_mark_unverified },
  failed: { tone: "review", label: m.settings_support_mark_unverified },
  unavailable: { tone: "review", label: m.settings_support_mark_unverified },
};

/** 冷却期内每秒重算一次，让「Resend in 42s」倒数；倒数到 0 就停掉定时器。 */
function useResendWaitSeconds(resendAvailableAt: string | null) {
  const [waitSeconds, setWaitSeconds] = useState(() =>
    readResendWaitSeconds(resendAvailableAt, Date.now()),
  );
  useEffect(() => {
    const tick = () => {
      const next = readResendWaitSeconds(resendAvailableAt, Date.now());
      setWaitSeconds(next);
      if (next === 0) clearInterval(timer);
    };
    const first = setTimeout(tick, 0);
    const timer = setInterval(tick, 1000);
    return () => {
      clearTimeout(first);
      clearInterval(timer);
    };
  }, [resendAvailableAt]);
  return waitSeconds;
}

/** 唯一支持邮箱与服务端验证状态；验证提示和重发按钮放在输入框下方的同一个框里。 */
export function HandoffSection({
  verification,
  savedEmail,
  errors,
  handoff,
  onChange,
}: HandoffSectionProps) {
  const resend = useAtomValue(resendSupportEmailAtom);
  const timeZone = useAtomValue(storeConnectionAtom).data?.timezone;
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState<SupportEmailSendResult>();
  const view = readSupportEmailVerificationView({
    draftEmail: handoff.supportEmail,
    savedEmail,
    verification,
    lastResult,
  });
  const waitSeconds = useResendWaitSeconds(verification.resendAvailableAt);
  const mark = MARKS[view.kind];

  const send = async () => {
    setBusy(true);
    const result = await resend.execute(undefined);
    setBusy(false);
    result.match(
      ({ outcome }) => {
        setLastResult(
          outcome === "sent" ||
            outcome === "failed" ||
            outcome === "unavailable"
            ? { email: savedEmail, result: outcome }
            : undefined,
        );
      },
      () => {
        setLastResult({ email: savedEmail, result: "failed" });
      },
    );
  };

  const hint =
    view.kind === "verified"
      ? m.settings_support_hint_verified()
      : view.kind === "unsaved"
        ? m.settings_support_hint_unsaved()
        : m.settings_support_email_hint();

  return (
    <Panel>
      <section className="px-5 py-4.75">
        <h2 className="m-0 mb-1 text-headline font-bold text-[var(--content-fg-strong)]">
          {m.settings_handoff_heading()}
        </h2>
        <p className="m-0 max-w-[62ch] text-label text-[var(--content-fg-subtle)]">
          {m.settings_handoff_copy()}
        </p>
        <div className="mt-4 flex max-w-[32.5rem] flex-col gap-3">
          <div className="relative">
            {mark && (
              <span className="absolute top-0.5 right-0">
                <StatusMark tone={mark.tone}>{mark.label()}</StatusMark>
              </span>
            )}
            <TextField
              error={errors.supportEmail}
              hint={hint}
              label={m.settings_support_email_label()}
              onChange={(event) => {
                onChange({ ...handoff, supportEmail: event.target.value });
              }}
              type="email"
              value={handoff.supportEmail}
            />
          </div>
          <VerificationNotice
            busy={busy}
            email={savedEmail}
            onSend={send}
            timeZone={timeZone}
            view={view}
            waitSeconds={waitSeconds}
          />
        </div>
      </section>
    </Panel>
  );
}

function VerificationNotice({
  view,
  email,
  busy,
  waitSeconds,
  timeZone,
  onSend,
}: {
  view: SupportEmailVerificationView;
  email: string;
  busy: boolean;
  waitSeconds: number;
  timeZone: string | undefined;
  onSend: () => void;
}) {
  if (
    view.kind === "empty" ||
    view.kind === "unsaved" ||
    view.kind === "verified"
  )
    return null;

  const error = view.kind === "failed" || view.kind === "unavailable";
  const resent = view.kind === "pending" && view.resent;
  const copy =
    view.kind === "failed"
      ? {
          title: m.settings_support_failed_title(),
          body: m.settings_support_failed_body(),
        }
      : view.kind === "unavailable"
        ? {
            title: m.settings_support_unavailable_title(),
            body: m.settings_support_unavailable_body(),
          }
        : view.kind === "unsent"
          ? {
              title: m.settings_support_unsent_title(),
              body: m.settings_support_unsent_body({ email }),
            }
          : resent
            ? {
                title: m.settings_support_resent_title(),
                body: m.settings_support_resent_body(),
              }
            : {
                title: m.settings_support_pending_title(),
                body: m.settings_support_pending_body({ email }),
              };
  const action = busy
    ? m.settings_support_sending()
    : waitSeconds > 0
      ? m.settings_support_resend_wait({ seconds: String(waitSeconds) })
      : error
        ? m.settings_support_try_again()
        : view.kind === "unsent"
          ? m.settings_support_send()
          : m.settings_support_resend();
  const sentAt =
    view.kind === "pending" && view.lastSentAt !== null && timeZone
      ? m.settings_support_sent_at({
          time: formatWorkspaceTime(view.lastSentAt, timeZone),
        })
      : null;

  return (
    <div
      role={error ? "alert" : "status"}
      className={cn(
        "flex items-start gap-3 rounded-[var(--radius)] border px-4 py-3.5",
        error
          ? "border-transparent bg-[var(--bg-vibrant-error-subtle)]"
          : "border-[var(--border-surface-default)] bg-[var(--bg-surface-subtle)]",
      )}
    >
      <span
        className={cn(
          "mt-px flex flex-none",
          error
            ? "text-[var(--content-vibrant-error-default)]"
            : resent
              ? "text-[var(--content-vibrant-success-default)]"
              : "text-[var(--content-fg-subtle)]",
        )}
      >
        <Icon name={error ? "alert" : resent ? "check" : "mail"} size={18} />
      </span>
      <div className="flex min-w-0 grow flex-col gap-1">
        <span
          className={cn(
            "text-title [font-weight:var(--font-weight-semibold)]",
            error
              ? "text-[var(--content-vibrant-error-strong)]"
              : "text-[var(--content-fg-strong)]",
          )}
        >
          {copy.title}
        </span>
        <span className="text-label [line-height:1.5] text-[var(--content-fg-default)]">
          {copy.body}
        </span>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <Button disabled={busy || waitSeconds > 0} onClick={onSend} size="sm">
            {action}
          </Button>
          {sentAt && (
            <span className="text-label text-[var(--content-fg-subtle)]">
              {sentAt}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
